import { useEntry, Loading, ErrorBox, PriorityChip, IntegrityNote } from "./entryShared";
import { Check, Minus, Flask, PlugsConnected } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TRANSFER = { high: "text-emerald-700", partial: "text-amber-600", low: "text-orange-600", none: "text-red-600" };
const CLS_META = {
  reuse_global: ["Reuse global", "bg-emerald-700 text-white"],
  localize: ["Localize", "bg-amber-500 text-white"],
  validate: ["Validate", "bg-blue-700 text-white"],
  search_local_data: ["Search local data", "bg-indigo-600 text-white"],
  primary_data_required: ["Primary data required", "bg-red-600 text-white"],
  expert_elicitation_required: ["Expert elicitation", "bg-orange-600 text-white"],
};
const STATUS_CLS = { complete: "bg-emerald-700 text-white", in_progress: "bg-blue-700 text-white", not_started: "bg-slate-200 text-slate-600" };

const ClsChip = ({ value }) => {
  const [label, cls] = CLS_META[value] || [value, "bg-slate-300 text-slate-700"];
  return <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${cls}`}>{label}</span>;
};

const Val = ({ value, source, country }) => (
  <div className="min-w-[120px]">
    {value ? <p className="text-[10px] text-slate-800 font-semibold">{value}</p>
      : <p className="text-[10px] text-slate-300">— no value (not fabricated)</p>}
    <div className="flex items-start gap-1 mt-0.5">
      {source ? <Check size={10} className="text-emerald-600 mt-0.5 shrink-0" /> : <Minus size={10} className="text-slate-300 mt-0.5 shrink-0" />}
      <span className={`text-[9px] ${source ? "text-slate-500" : "text-slate-300"}`}>{source || "No source"}{country ? ` · ${country.replaceAll("_", " ")}` : ""}</span>
    </div>
  </div>
);

const READY_BAR = (p) => (p >= 70 ? "bg-emerald-600" : p >= 40 ? "bg-amber-500" : "bg-red-500");

export const CaseEconomics = ({ caseId }) => {
  const { data, error } = useEntry(caseId, "/localization-studio");
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading label="Loading localization studio…" />;
  const empty = !data.groups?.length;
  return (
    <div data-testid="case-economics" className="mt-4">
      <div className="bg-slate-900 text-white px-5 py-4 mb-4" data-testid="model-status-note">
        <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
          <Flask size={13} /> Economic Model Localization Studio — model status: {data.model_status?.replaceAll("_", " ")}
        </p>
        <p className="text-[11px] text-white/70 mt-1 leading-relaxed">{data.model_note}</p>
        <p className="text-[11px] text-white/50 mt-1 leading-relaxed">{data.source_market_note}</p>
      </div>

      {empty ? (
        <p className="text-xs text-slate-400 border border-dashed border-slate-300 bg-white px-4 py-8 text-center" data-testid="localization-empty">
          Parameter localization table not yet developed for this case.</p>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <div className="border border-slate-200 bg-white p-5" data-testid="studio-overall">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Localization completeness</p>
              <p className="font-heading text-4xl font-bold text-slate-900 mt-1">{data.overall_ready_pct}<span className="text-base text-slate-400">%</span></p>
              <p className="text-[10px] text-slate-400">across {data.groups.reduce((n, g) => n + g.parameters.length, 0)} model parameters</p>
            </div>
            <div className="border border-slate-200 bg-white p-5 md:col-span-2" data-testid="studio-completeness">
              {(data.completeness || []).map((c) => (
                <div key={c.group} className="flex items-center gap-3 mb-1.5" data-testid={`completeness-${c.group.replaceAll(" ", "-").toLowerCase()}`}>
                  <span className="w-36 shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-600 text-right">{c.group}</span>
                  <div className="flex-1 h-2.5 bg-slate-100"><div className={`h-full ${READY_BAR(c.ready_pct)}`} style={{ width: `${c.ready_pct}%` }} /></div>
                  <span className="w-10 text-right font-heading font-bold text-sm text-slate-900">{c.ready_pct}%</span>
                  <span className="w-44 shrink-0 text-[9px] text-slate-400 hidden lg:block">{c.basis}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-5" data-testid="studio-classification-counts">
            {Object.entries(data.classification_counts || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5"><ClsChip value={k} /><span className="font-heading font-bold text-slate-900 text-sm">{v}</span></div>
            ))}
          </div>

          {(data.groups || []).map((g) => (
            <div key={g.group} className="mb-5" data-testid={`studio-group-${g.group.replaceAll(" ", "-").toLowerCase()}`}>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">{g.group}</p>
              <div className="overflow-x-auto border border-slate-200 bg-white">
                <table className="w-full text-left">
                  <thead><tr className="bg-slate-50 text-[9px] uppercase tracking-widest text-slate-400">
                    <th className="px-3 py-2">Parameter · Category</th><th className="px-3 py-2">Classification</th>
                    <th className="px-3 py-2">Global value · Source</th><th className="px-3 py-2">Target value · Source</th>
                    <th className="px-3 py-2">Transfer</th><th className="px-3 py-2">Data availability</th>
                    <th className="px-3 py-2">Priority · Impact</th><th className="px-3 py-2">Recommended source</th>
                    <th className="px-3 py-2">Collection method</th><th className="px-3 py-2">Owner</th><th className="px-3 py-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {g.parameters.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100 align-top" data-testid={`studio-${p.id}`}>
                        <td className="px-3 py-2 min-w-[170px]">
                          <p className="text-[11px] font-bold text-slate-800">{p.parameter}</p>
                          <p className="text-[9px] uppercase tracking-wide text-slate-400">{p.studio_category}</p>
                        </td>
                        <td className="px-3 py-2"><ClsChip value={p.classification} /></td>
                        <td className="px-3 py-2"><Val value={p.global_value} source={p.global_source} country={p.source_country} /></td>
                        <td className="px-3 py-2"><Val value={p.local_value} source={p.local_source} /></td>
                        <td className={`px-3 py-2 text-[10px] font-bold uppercase ${TRANSFER[p.transferability] || "text-slate-400"}`}>{p.transferability}</td>
                        <td className="px-3 py-2 text-[10px] uppercase text-slate-600">{p.data_availability}</td>
                        <td className="px-3 py-2 whitespace-nowrap"><PriorityChip value={p.priority} /><p className="text-[9px] uppercase text-slate-500 mt-0.5">impact: {p.sensitivity}</p></td>
                        <td className="px-3 py-2 text-[10px] text-slate-600 min-w-[130px]">{p.recommended_data_source}</td>
                        <td className="px-3 py-2 text-[10px] text-slate-600 min-w-[140px]">{p.collection_method}</td>
                        <td className="px-3 py-2 text-[10px] font-bold text-slate-700">{p.owner}</td>
                        <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase whitespace-nowrap ${STATUS_CLS[p.status]}`}>{p.status?.replaceAll("_", " ")}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div className="border border-slate-200 bg-white p-5" data-testid="studio-connections">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
              <PlugsConnected size={13} className="text-emerald-600" /> Structured gaps flow into the platform — nothing is fabricated
            </p>
            <div className="grid md:grid-cols-3 gap-4 mt-3 text-[11px]">
              <div data-testid="connections-gaps">
                <p className="text-[9px] uppercase tracking-widest text-slate-400">Gap Analysis rows fed</p>
                <p className="text-slate-700 mt-0.5">{(data.connections?.gap_analysis || []).map((g) => g.replaceAll("_", " ")).join(" · ") || "None"}</p>
                <p className="text-[10px] text-slate-400 mt-1">{data.structured_gaps?.length} open parameter gaps · see the Gaps tab and <Link to="/console/gaps" className="underline text-slate-700">Gap Analysis</Link></p>
              </div>
              <div data-testid="connections-plan">
                <p className="text-[9px] uppercase tracking-widest text-slate-400">Evidence generation plan (workplan phases 3–5)</p>
                {(data.connections?.evidence_generation_plan || []).slice(0, 4).map((a, i) => (
                  <p key={i} className="text-slate-700 mt-0.5">— {a.activity}</p>
                ))}
              </div>
              <div data-testid="connections-hub">
                <p className="text-[9px] uppercase tracking-widest text-slate-400">Knowledge Hub & Copilot</p>
                <p className="text-slate-700 mt-0.5"><Link to="/console/knowledge" className="underline">Knowledge Hub</Link> holds the underlying sources.</p>
                <p className="text-slate-500 mt-1">{data.connections?.copilot}</p>
              </div>
            </div>
          </div>
        </>
      )}
      <IntegrityNote text={data.integrity_note} />
    </div>
  );
};
